import { MouseEvent, MouseEventHandler } from 'react';

interface RowMouseHandlerOptions<TData> {
  row: TData;
  onRowClick?: (row: TData) => void;
  getRowHref?: (row: TData) => string | undefined;
}

/**
 * Mouse handlers for a clickable table cell that support "open in a new tab".
 *
 * Row navigation historically ran through a JS `onRowClick` → `navigate()`, so
 * CMD/CTRL+click and middle-click did nothing special. When the screen provides
 * `getRowHref`, those gestures open the row's detail route in a new tab while a
 * plain click keeps the existing `onRowClick` behavior.
 *
 * In-cell interactive content (links, action buttons) keeps working: it already
 * stops propagation before these handlers fire.
 */
export function buildRowMouseHandlers<TData>({
  row,
  onRowClick,
  getRowHref,
}: RowMouseHandlerOptions<TData>): {
  onClick: MouseEventHandler;
  onAuxClick: MouseEventHandler;
} {
  const openInNewTab = (): boolean => {
    const href = getRowHref?.(row);
    if (!href) return false;
    window.open(href, '_blank', 'noopener,noreferrer');
    return true;
  };

  return {
    onClick: (event: MouseEvent) => {
      if ((event.metaKey || event.ctrlKey) && openInNewTab()) return;
      onRowClick?.(row);
    },
    onAuxClick: (event: MouseEvent) => {
      // Middle-click only — right-click stays reserved for context menus.
      if (event.button !== 1) return;
      if (openInNewTab()) event.preventDefault();
    },
  };
}
